import { useToast } from '@chakra-ui/react';
import { useState, useMemo } from 'react';

import {
  utils as commonUtils,
  date as commonDate,
} from '@finley-technologies/common';
import { CreditAgreement } from '@finley-technologies/finley-types';

import { ConcentrationLimitInsightsGqlDataGQLType } from '../../../../../types/dashboard-core-gqlTypes.generated.d';
import { useGetDailyConcentrationInsightsQuery } from '../../requests/requests.gql.generated';
import {
  chartsInitialEndDateJs,
  chartsInitialStartDateJs,
  ChartTimePeriod,
  defaultSelectedTimePeriod,
  formatChartDataByReportingDate,
  formatConcentrationLimitDataForKey,
  getConcentrationLimitFormatsByKey,
  ConcentrationLimitFormatMap,
} from '../shared/portfolioChartUtils';
import { errorToastConfig } from '~/packages/common-frontend/constants';
import { getUnexpectedErrorMessage } from '~/packages/common-frontend/utils';

export const useConcentrationLimitsData = (creditFacilityId: string) => {
  const toast = useToast();

  // Shared state for concentration limits charts
  const [chartDataDateRange, setChartDataDateRange] = useState<{
    chartDataStartDate: Date;
    chartDataEndDate: Date;
  }>({
    chartDataStartDate: chartsInitialStartDateJs,
    chartDataEndDate: chartsInitialEndDateJs,
  });

  const [selectedTimePeriod, setSelectedTimePeriod] = useState<ChartTimePeriod>(
    defaultSelectedTimePeriod,
  );

  // Store formatted data for each concentration limit key
  const [
    concentrationLimitChartData,
    setConcentrationLimitChartData,
  ] = useState<Map<string, ConcentrationLimitInsightsGqlDataGQLType[]>>(
    new Map(),
  );

  const [
    concentrationLimitChartDataForDateRange,
    setConcentrationLimitChartDataForDateRange,
  ] = useState<Map<string, ConcentrationLimitInsightsGqlDataGQLType[]>>(
    new Map(),
  );

  const {
    data: concentrationData,
    loading: isConcentrationDataLoading,
    refetch: refetchConcentrationInsightsData,
  } = useGetDailyConcentrationInsightsQuery({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = getUnexpectedErrorMessage(err);
      toast(errorToastConfig);
    },
    variables: {
      creditFacilityParams: {
        creditFacilityId,
        reportingDateStart: commonDate.dateToFullDateString(
          chartDataDateRange.chartDataStartDate,
        ),
        reportingDateEnd: commonDate.dateToFullDateString(
          chartDataDateRange.chartDataEndDate,
        ),
      },
    },
    onCompleted: (data) => {
      const concentrationLimitFormatsByKey = getConcentrationLimitFormatsByKey(
        data.getDailyConcentrationInsights,
      );

      const chartDataByKey = new Map<
        string,
        ConcentrationLimitInsightsGqlDataGQLType[]
      >();
      const chartDataForDateRangeByKey = new Map<
        string,
        ConcentrationLimitInsightsGqlDataGQLType[]
      >();

      // Process data for each concentration limit
      concentrationLimitFormatsByKey.forEach((_valueType, limitKey) => {
        const concentrationLimitDataForKey = formatConcentrationLimitDataForKey(
          data.getDailyConcentrationInsights,
          limitKey,
        );

        // Format data by day (base data)
        const formattedChartData = formatChartDataByReportingDate({
          chartData: concentrationLimitDataForKey,
          selectedTimePeriod: ChartTimePeriod.day,
        });
        chartDataForDateRangeByKey.set(limitKey, formattedChartData);

        // Format data for the selected time period
        if (selectedTimePeriod !== ChartTimePeriod.day) {
          const formattedChartDataForPeriod = formatChartDataByReportingDate({
            chartData: concentrationLimitDataForKey,
            selectedTimePeriod,
          });
          chartDataByKey.set(limitKey, formattedChartDataForPeriod);
        } else {
          chartDataByKey.set(limitKey, formattedChartData);
        }
      });

      setConcentrationLimitChartDataForDateRange(chartDataForDateRangeByKey);
      setConcentrationLimitChartData(chartDataByKey);
    },
    notifyOnNetworkStatusChange: true,
  });

  const concentrationLimitFormatMap = useMemo<ConcentrationLimitFormatMap>(() => {
    if (!concentrationData) {
      return new Map();
    }
    return getConcentrationLimitFormatsByKey(
      concentrationData.getDailyConcentrationInsights,
    );
  }, [concentrationData]);

  // Handler for time period selection
  const onSelectTimePeriod = (timePeriod: ChartTimePeriod) => {
    setSelectedTimePeriod(timePeriod);

    const chartDataByKey = new Map<
      string,
      ConcentrationLimitInsightsGqlDataGQLType[]
    >();

    concentrationLimitChartDataForDateRange.forEach((chartData, limitKey) => {
      const formattedChartData = formatChartDataByReportingDate({
        chartData,
        selectedTimePeriod: timePeriod,
      });
      chartDataByKey.set(limitKey, formattedChartData);
    });

    setConcentrationLimitChartData(chartDataByKey);
  };

  return {
    // State
    chartDataDateRange,
    setChartDataDateRange,
    selectedTimePeriod,
    concentrationLimitChartData,
    concentrationLimitFormatMap,

    // Query data
    concentrationData,
    isConcentrationDataLoading,
    refetchConcentrationInsightsData,

    // Handlers
    onSelectTimePeriod,
  };
};

/**
 * Hook to create a mapping of concentration limit keys to chart titles
 * and prepare combined CSV data for all concentration limits
 */
export const useConcentrationLimitsMetadata = (
  concentrationLimitFormatMap: ConcentrationLimitFormatMap,
  creditAgreementConcentrationLimits: CreditAgreement.DomainEntity.ConcentrationLimitProperty[],
  concentrationLimitChartData: Map<
    string,
    ConcentrationLimitInsightsGqlDataGQLType[]
  >,
) => {
  // Create a mapping of concentrationLimitKey to chartTitle
  const limitKeyToTitleMap = useMemo(() => {
    const map = new Map<string, string>();
    Array.from(concentrationLimitFormatMap.keys()).forEach((limitKey) => {
      const creditAgreementConcentrationLimit = creditAgreementConcentrationLimits.find(
        (concentrationLimit) =>
          concentrationLimit._config_.concentrationLimitConfig?.propertyKey.toLowerCase() ===
          limitKey.toLowerCase(),
      );
      const title =
        creditAgreementConcentrationLimit?.name ??
        commonUtils.toTitleCase(limitKey);
      map.set(limitKey, title);
    });
    return map;
  }, [concentrationLimitFormatMap, creditAgreementConcentrationLimits]);

  // Prepare combined CSV data for all concentration limits
  const combinedCsvData = useMemo(() => {
    if (concentrationLimitChartData.size === 0) {
      return { dataRows: [], headers: [] };
    }

    const allDataRows: any[] = [];
    const headerSet = new Set<string>();
    headerSet.add('concentrationLimitName');
    headerSet.add('reportingDate');

    concentrationLimitChartData.forEach((chartData, limitKey) => {
      const chartTitle = limitKeyToTitleMap.get(limitKey) || limitKey;

      // Add rows for each concentration limit
      chartData.forEach((dataPoint) => {
        const row: any = {
          concentrationLimitName: chartTitle,
          reportingDate: dataPoint.reportingDate,
        };

        // Add all other fields from the data point
        Object.keys(dataPoint).forEach((key) => {
          if (key !== 'reportingDate') {
            headerSet.add(key);
            row[key] = (dataPoint as any)[key];
          }
        });

        allDataRows.push(row);
      });
    });

    const headers = Array.from(headerSet).map((key) => ({
      label: key,
      key,
    }));

    return {
      dataRows: allDataRows,
      headers,
    };
  }, [concentrationLimitChartData, limitKeyToTitleMap]);

  return {
    limitKeyToTitleMap,
    combinedCsvData,
  };
};
